import type { ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { viewportOnce } from "../../lib/motion";

type RevealProps = {
  children: ReactNode;
  delay?: number;
  className?: string;
  y?: number;
  /** Optional horizontal offset for directional reveals. */
  x?: number;
  /** Adds a soft focus-pull blur as the element enters. */
  blur?: boolean;
  duration?: number;
};

const EASE = [0.22, 1, 0.36, 1] as const;

/** Scroll-triggered fade + rise reveal wrapper. */
export function Reveal({
  children,
  delay = 0,
  className,
  y = 40,
  x = 0,
  blur = true,
  duration = 0.85,
}: RevealProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y, x, filter: blur ? "blur(10px)" : "blur(0px)" }}
      whileInView={{ opacity: 1, y: 0, x: 0, filter: "blur(0px)" }}
      viewport={viewportOnce}
      transition={{ duration, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

type RevealTextProps = {
  text: string;
  className?: string;
  delay?: number;
};

/** Splits a string into words and reveals each with a masked rise. */
export function RevealText({ text, className, delay = 0 }: RevealTextProps) {
  const words = text.split(" ");
  const reduce = useReducedMotion();

  if (reduce) {
    return <span className={className}>{text}</span>;
  }

  return (
    <motion.span
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      aria-label={text}
    >
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden pb-[0.12em] align-bottom">
          <motion.span
            className="inline-block will-change-transform"
            initial={{ y: "110%", rotate: 5, opacity: 0 }}
            whileInView={{ y: "0%", rotate: 0, opacity: 1 }}
            viewport={viewportOnce}
            transition={{
              duration: 0.85,
              ease: EASE,
              delay: delay + i * 0.045,
            }}
          >
            {word}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
}
